/**
 * Bootstrap.
 *
 * One build serves all three targets. The layout switch is a media query on the
 * viewport, so the desktop browser, a phone browser and the Capacitor shells all
 * run the same code and pick the layout that fits.
 */

import './style.css';
import { h, mount } from './vdom.js';
import * as store from './store.js';
import { strings } from './i18n.js';
import { createFormatter } from './fmt.js';
import { setNumberReading } from '../../shared/model.js';
import { createInputs, C, SANS, css, setRemoveLabel } from './views/common.js';
import { renderDesktop } from './views/desktop.js';
import { renderMobile } from './views/mobile.js';
import { renderAuth, renderClaimDialog, renderToast, renderTermsGate, renderGiftNotice } from './views/auth.js';
import { renderSettings } from './views/settings.js';
import { renderAdmin } from './views/admin.js';
import { renderImport } from './views/import.js';

const MOBILE_QUERY = window.matchMedia('(max-width: 900px)');
const inputs = createInputs();

/*
 * The advertising frame reporting that it ran.
 *
 * Sandboxed without allow-same-origin, so its origin is "null" and cannot be
 * checked — which is fine here precisely because the message does nothing but
 * keep a slot on screen. Anything else arriving on this channel is ignored.
 */
window.addEventListener('message', event => {
  if (event.data && event.data.pt === 'ad-frame-ready') store.adFrameLoaded();
});

const actions = {
  setTab: store.setTab,
  stepMonth: store.stepMonth,
  goToMonth: store.goToMonth,
  setLanguage: store.setLanguage,
  setSheet: store.setSheet,
  setSettings: store.setSettings,
  dismissError: store.dismissError,
  undoDelete: store.undoDelete,
  dismissUndo: store.dismissUndo,
  showError: store.showError,
  setAuthMode: store.setAuthMode,
  openClaim: store.openClaim,
  closeClaim: store.closeClaim,
  openSettings: store.openSettings,
  setSettingsSection: store.setSettingsSection,
  addSettingsCurrency: store.addSettingsCurrency,
  forgetAddedCurrency: store.forgetAddedCurrency,
  toggleAccountMenu: store.toggleAccountMenu,
  copyVersion: store.copyVersion,
  dismissGift: store.dismissGift,
  closeSettings: store.closeSettings,
  openAdmin: store.openAdmin,
  closeAdmin: store.closeAdmin,
  loadAdmin: store.loadAdmin,
  setAdminQuery: store.setAdminQuery,
  adminSetPlan: store.adminSetPlan,
  setJournalMode: store.setJournalMode,
  setCushionMonths: store.setCushionMonths,
  setPropTrader: store.setPropTrader,
  setWhatIf: store.setWhatIf,
  setWhatIfPreset: store.setWhatIfPreset,
  setWhatIfHover: store.setWhatIfHover,
  openWhatIf: store.openWhatIf,
  setTrendHover: store.setTrendHover,
  toggleUpcomingAll: store.toggleUpcomingAll,
  setCashUi: store.setCashUi,
  setDebtUi: store.setDebtUi,
  setInvUi: store.setInvUi,
  loadMetalPrice: store.loadMetalPrice,
  setVehUi: store.setVehUi,
  setJournalUi: store.setJournalUi,
  setBacktestUi: store.setBacktestUi,
  updateBacktest: store.updateBacktest,
  setDebtExtra: store.setDebtExtra,
  loadReview: store.loadReview,
  requestReview: store.requestReview,
  setPitYear: store.setPitYear,
  setPitInput: store.setPitInput,
  convertPitTrades: store.convertPitTrades,
  setTaxCountry: store.setTaxCountry,
  setTaxInput: store.setTaxInput,
  setBudget: store.setBudget,
  setPropRulesEdit: store.setPropRulesEdit,
  setPropUi: store.setPropUi,
  ensureRates: store.ensureRates,
  loadPush: store.loadPush,
  enablePush: store.enablePush,
  disablePush: store.disablePush,
  setPushPrefs: store.setPushPrefs,
  testPush: store.testPush,
  openPropSettings: store.openPropSettings,
  setBudgetUi: store.setBudgetUi,
  setGoalUi: store.setGoalUi,
  addGoal: store.addGoal,
  updateGoal: store.updateGoal,
  deleteGoal: store.deleteGoal,
  loadTaxVotes: store.loadTaxVotes,
  toggleTaxVote: store.toggleTaxVote,
  selectBacktest: store.selectBacktest,
  addBacktest: store.addBacktest,
  askDeleteBacktest: store.askDeleteBacktest,
  removeBacktest: store.removeBacktest,
  loadProp: store.loadProp,
  addPropAccount: store.addPropAccount,
  updatePropAccount: store.updatePropAccount,
  askDeletePropAccount: store.askDeletePropAccount,
  removePropAccount: store.removePropAccount,
  addPropEvent: store.addPropEvent,
  removePropEvent: store.removePropEvent,

  startDiscordSignIn: store.startDiscordSignIn,
  enterLedger: store.enterLedger,
  resetAuth: store.resetAuth,
  requestReset: store.requestReset,
  submitReset: store.submitReset,
  cancelReset: store.cancelReset,
  unlinkDiscord: store.unlinkDiscord,
  setPassword: store.setPassword,
  togglePassword: store.togglePassword,
  openMonthPicker: store.openMonthPicker,
  closeMonthPicker: store.closeMonthPicker,
  stepMonthPickerYear: store.stepMonthPickerYear,
  resendVerification: store.resendVerification,
  updateVehicle: store.updateVehicle,
  setFuelPartial: store.setFuelPartial,
  setEditFuelFull: store.setEditFuelFull,
  setCashBalance: store.setCashBalance,
  setInflation: store.setInflation,
  removeInflation: store.removeInflation,
  setRate: store.setRate,
  removeRate: store.removeRate,
  rateRow: store.rateRow,
  rateHistory: store.rateHistory,
  refreshRates: store.refreshRates,
  backfillRates: store.backfillRates,
  addRecurring: store.addRecurring,
  updateRecurring: store.updateRecurring,
  removeRecurring: store.removeRecurring,
  postRecurring: store.postRecurring,
  pendingRecurring: store.pendingRecurring,
  recurringInMonth: store.recurringInMonth,
  askRecurringAgain: store.askRecurringAgain,
  closeRecurringAgain: store.closeRecurringAgain,
  fixedCostSummary: store.fixedCostSummary,
  upcomingCharges: store.upcomingCharges,
  setEditDate: store.setEditDate,
  loadAiSettings: store.loadAiSettings,
  categoriseImport: store.categoriseImport,
  startCheckout: store.startCheckout,
  openBillingPortal: store.openBillingPortal,
  openImport: store.openImport,
  closeImport: store.closeImport,
  confirmImport: store.confirmImport,
  setImportColumn: store.setImportColumn,
  setImportKind: store.setImportKind,
  setImportCategory: store.setImportCategory,
  setImportMonthMode: store.setImportMonthMode,
  showAllImportRows: store.showAllImportRows,
  toggleImportRow: store.toggleImportRow,
  exportCsv: store.exportCsv,
  setPriceRegion: store.setPriceRegion,
  exportAll: store.exportAll,
  setTermsTicked: store.setTermsTicked,
  acceptTerms: store.acceptTerms,
  askDeleteAccount: store.askDeleteAccount,
  deleteAccount: store.deleteAccount,
  setEntryCurrency: store.setEntryCurrency,
  entryCurrency: store.entryCurrency,

  signIn: store.signIn,
  signUp: store.signUp,
  signInAsGuest: store.signInAsGuest,
  signOut: store.signOut,
  claimGuestAccount: store.claimGuestAccount,
  claimFresh: store.claimFresh,
  setClaimFresh: store.setClaimFresh,

  addIncome: store.addIncome,
  removeIncome: store.removeIncome,
  addExpense: store.addExpense,
  removeExpense: store.removeExpense,
  addDebt: store.addDebt,
  removeDebt: store.removeDebt,
  payDebt: store.payDebt,
  addHolding: store.addHolding,
  removeHolding: store.removeHolding,
  setHoldingSince: store.setHoldingSince,
  openDatePicker: store.openDatePicker,
  setPickedDate: store.setPickedDate,
  setFuelDate: store.setFuelDate,
  setCarExpenseDate: store.setCarExpenseDate,
  stepDatePickerMonth: store.stepDatePickerMonth,
  loadAds: store.loadAds,
  setAdConsent: store.setAdConsent,
  adFrameLoaded: store.adFrameLoaded,
  adFrameBlocked: store.adFrameBlocked,
  loadTrades: store.loadTrades,
  retryTrades: store.retryTrades,
  addTrade: store.addTrade,
  openTradeImport: store.openTradeImport,
  setTradeImport: store.setTradeImport,
  closeTradeImport: store.closeTradeImport,
  confirmTradeImport: store.confirmTradeImport,
  updateTrade: store.updateTrade,
  removeTrade: store.removeTrade,
  sellHolding: store.sellHolding,
  setHoldingClass: store.setHoldingClass,
  askHoldingExit: store.askHoldingExit,
  closeHoldingExit: store.closeHoldingExit,
  askEdit: store.askEdit,
  closeEdit: store.closeEdit,
  setEditClass: store.setEditClass,
  saveEdit: store.saveEdit,
  saveTradeEdit: store.saveTradeEdit,
  addFuel: store.addFuel,
  removeFuel: store.removeFuel,
  addCarExpense: store.addCarExpense,
  removeCarExpense: store.removeCarExpense,
  refreshPrices: store.refreshPrices,
  backfillPrices: store.backfillPrices
};

function buildContext(state) {
  const mobile = MOBILE_QUERY.matches;
  const t = strings(state.lang, { mobile });
  /* "1,750" is a rent in English and one and three quarters in Polish -- see parseNumber. */
  setNumberReading(state.lang);
  const fmt = createFormatter({
    lang: state.lang,
    baseCurrency: state.settings.baseCurrency,
    hideBalances: state.settings.hideBalances,
    unitSystem: state.settings.unitSystem,
    t
  });
  setRemoveLabel(t.shell.removeLabel);
  return { state, metrics: state.metrics, t, fmt, inputs, actions, mobile };
}

function splash(message) {
  return h('div', {
    style: css({
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: C.bg, color: C.faint, fontFamily: SANS, fontSize: '13px'
    })
  }, message);
}

function render(state) {
  /*
   * The document says which language it is in. It was fixed at "en" in
   * index.html, so a browser showing the Polish interface offered to translate
   * it from English, and a screen reader read Polish with English rules.
   */
  const lang = state.lang === 'PL' ? 'pl' : 'en';
  if (document.documentElement.lang !== lang) document.documentElement.lang = lang;

  const ctx = buildContext(state);

  /*
   * The reset panel outranks the splash. Following a reset link while already
   * signed in used to hang here: the session resumes, status stays 'loading',
   * and the panel the link exists to show never gets a turn.
   */
  if (state.status === 'loading' && state.authPhase !== 'reset') {
    return splash(ctx.t.shell.loading);
  }

  // The Discord confirmation card is shown before the ledger loads, so the
  // sign-in panel stays on screen while `authPhase` is anything but idle.
  if (state.status === 'anon' || state.authPhase !== 'idle' || !state.metrics) {
    return h('div', null, renderAuth(ctx), renderToast(ctx));
  }

  /*
   * Keyed because three of these four are conditional. Index-based diffing
   * would otherwise rewrite one overlay's element into another's when one
   * closes, carrying its click handler with it.
   */
  return h('div', null,
    ctx.mobile ? renderMobile(ctx) : renderDesktop(ctx),
    state.settingsOpen && renderSettings(ctx),
    state.adminOpen && state.user?.isAdmin && renderAdmin(ctx),
    state.importing && renderImport(ctx),
    state.claimOpen && renderClaimDialog(ctx),
    /*
     * Accounts that never saw a sign-up form accept the terms once, here. Only
     * on an explicit false: a session restored from before this existed has no
     * answer at all until the server has been asked.
     */
    state.user && !state.user.isGuest && state.user.termsAccepted === false && renderTermsGate(ctx),
    /*
     * The launch gift, announced once per account -- after the terms, never on
     * top of them, since those have to be dealt with first.
     */
    state.user && !state.user.isGuest && state.user.termsAccepted === true
      && state.user.plan?.source === 'gift' && state.user.giftSeen === false && renderGiftNotice(ctx),
    renderToast(ctx)
  );
}

const update = mount(document.getElementById('app'), render);

store.subscribe(update);
update(store.getState());
store.start();

// A layout change is a state change as far as the renderer is concerned.
MOBILE_QUERY.addEventListener('change', () => update(store.getState()));

// Escape backs out of whatever is layered on top, innermost first.
document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return;
  const state = store.getState();

  if (state.recurringAgain) store.closeRecurringAgain();
  else if (state.monthPicker) store.closeMonthPicker();
  else if (state.claimOpen) store.closeClaim();
  else if (state.importing) store.closeImport();
  else if (state.accountMenuOpen) store.toggleAccountMenu(false);
  else if (state.settingsOpen) store.closeSettings();
  else if (state.adminOpen) store.closeAdmin();
  else if (state.sheet) store.setSheet(null);
});

// Coming back to a backgrounded tab (or app) is the natural moment to resync.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && store.getState().status === 'ready') {
    store.flush().then(() => store.refresh({ silent: true }));
  }
});

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('sw.js').catch(() => {
      // Offline caching is a bonus; the app works without it.
    });
  });
}
